import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import './TableFilter.scss';

import classnames from 'classnames';

import { IApplicationState } from '@store';

import { filterItemList } from '../ResultsTable/ResultsTable';
import { FilterTextInput } from '../FilterTextInput/FilterTextInput';
export interface IResultsFilter {
  finishedDayRange: string;
  sampleName: string;
  description: string;
  externalSubjectId: string;
  qcStatus: string;
  orderNumber: string;
  specimenNumber: string;
  resultName: string;
}
export interface IResultsTableFilter extends ITableFilter {
  filters: any;
  activeFilters: any;
}

export interface ITableFilter {
  isOpen: boolean;
  isDisabled: boolean;
  toggleOpen: Function;
  toggleDisabled: Function;
  update: Function;
  save: Function;
  clear: Function;
  clearSavedFilter: Function;
  clearAll: Function;
}
type FinishedDayRangeType = {
  [key: string]: string;
};

let finishedDayRange: FinishedDayRangeType = {
  '0': 'Awaiting Results',
  '2': 'Last 2 Days',
  '7': 'Last 7 Days',
  '30': 'Last 30 Days',
};

export const filterTraverse = (obj: any) => {
  for (let k in obj) {
    if (obj[k] && typeof obj[k] === 'object') {
      filterTraverse(obj[k]);
    } else {
      return obj[k];
    }
  }
  return obj;
};

const filterLabelMappings: any = {
  finishedDayRange: 'Results Activity',
  sampleName: 'Sample Name',
  description: 'Description',
  externalSubjectId: 'Patient ID',
  qcStatus: 'QC Status',
  orderNumber: 'Order Number',
  specimenNumber: 'Sample ID',
  resultName: 'Report ID',
  pkgId: 'Package ID',
  pkgName: 'Package Name',
};

export const ResultsTableFilter: React.FC<IResultsTableFilter> = ({
  isOpen,
  isDisabled,
  filters,
  activeFilters,
  toggleOpen,
  toggleDisabled,
  update,
  save,
  clear,
  clearSavedFilter,
  clearAll,
}) => {
  const history = useHistory();

  const prefix = useSelector(
    (state: IApplicationState) => state.projectPicker.reportNumberPrefix
  );
  const { t } = useTranslation();
  const isSampleLevelDocumentsEnabled = useSelector(
    (state: IApplicationState) => state.projectPicker.hasSampleLevelDocuments
  );
  const [isRender, setIsRendered] = React.useState(0);

  React.useEffect(() => {
    history.listen(() => {
      if (history.location.pathname !== '/results') {
        clearAll();
      }
    });
  }, [history]);

  const loadActiveFilters = () => {
    let newFilters: JSX.Element[] = [];
    Object.keys(activeFilters).forEach((key, i) => {
      let value = activeFilters[key];
      if (value !== null && key !== filterItemList.pkgId) {
        if (key === 'finishedDayRange') {
          value = finishedDayRange[value];
        }

        const dots = value.length > 50 ? '...' : '';
        const truncatedValue =
          _.truncate(value, {
            length: 50,
            omission: '',
          }) + dots;

        newFilters.push(
          <div className='active-filter-items' key={i} title={value}>
            <span className='item-label'>{filterLabelMappings[key]}</span>
            <span
              className={classnames('item-value', {
                'text-uppercase': key === 'qcStatus',
              })}
            >
              {truncatedValue}
            </span>
            <span
              className='filter-item-icon'
              onClick={() => clearSavedFilter(key)}
            >
              <i className='fas fa-times'></i>
            </span>
          </div>
        );
      }
    });
    return newFilters;
  };

  React.useEffect(() => {
    setIsRendered(isRender + 1);
  }, []);

  return (
    <div id='table-filter' key={isRender}>
      <div id='filter-toggle'>
        <div id='filter-item-controls'>
          {!isDisabled ? (
            <div
              key={Math.random()}
              id='filter-by-control'
              onClick={() => toggleOpen()}
            >
              {isOpen ? (
                <span>
                  <i className='fas fa-caret-down mr-1 fcolor--primary-brand' />
                </span>
              ) : (
                <span>
                  <i className='fas fa-caret-right mr-1 fcolor--primary-brand' />
                </span>
              )}
              <span className='fcolor--primary-brand' id='filter-by'>
                {t('results_filters_text_filterBy')}
              </span>
            </div>
          ) : null}

          <div id='active-filter-list' className={isOpen ? 'hide' : 'show'}>
            {loadActiveFilters()}
          </div>
        </div>
      </div>
      <div id='table-filter-form' className={isOpen ? 'open' : 'closed'}>
        <div id='left'>
          <div id='results-activity' className='dropdown'>
            <label>{t('results_filters_text_filterActivity')}</label>
            <select
              value={filters.finishedDayRange}
              name='results-activity'
              id='results-activity-dropdown'
              onChange={(e) => update('finishedDayRange', e.target.value)}
            >
              <option value=''></option>
              <option value='2'>{t('results_filters_activity_1')}</option>
              <option value='7'>{t('results_filters_activity_2')}</option>
              <option value='30'>{t('results_filters_activity_3')}</option>
              <option value='0'>{t('results_filters_activity_4')}</option>
            </select>
          </div>
          <div id='controls'>
            <button
              id='filter-btn'
              className='btn btn-primary'
              onClick={() => save()}
            >
              {t('results_filters_text_filterList')}
            </button>
          </div>
        </div>
        <div id='middle'>
          <div id='middle-left'>
            <div id='sample-name' className='input'>
              <label>{t('results_filters_labels_sampleName')}</label>
              <FilterTextInput
                value={filters.sampleName}
                name='sampleName'
                callback={update}
                config={{ placeholder: '', maxLength: 256 }}
                save={save}
              />
            </div>
            <div id='description' className='input'>
              <label>{t('results_filters_labels_description')}</label>
              <FilterTextInput
                value={filters.description}
                name='description'
                callback={update}
                config={{ placeholder: '', maxLength: 256 }}
                save={save}
              />
            </div>
          </div>
          <div id='middle-right'>
            <div id='patient-id' className='input'>
              <label>{t('results_filters_labels_patientId')}</label>
              <FilterTextInput
                value={filters.externalSubjectId}
                key={isRender}
                name='externalSubjectId'
                callback={update}
                config={{ placeholder: '', maxLength: 256 }}
                save={save}
              />
            </div>
            <div id='qc-status' className='dropdown'>
              <label>{t('results_filters_labels_qcStatus')}</label>
              <select
                className='text-uppercase'
                value={filters.qcStatus}
                name='qc-status'
                id='qc-status-dropdown'
                onChange={(e) => update('qcStatus', e.target.value)}
              >
                <option value=''></option>
                <option value='Cancelled'>
                  {t('results_filters_qcStatus_cancelled')}
                </option>
                <option value='Fail'>
                  {t('results_filters_qcStatus_failed')}
                </option>
                <option value='Pass'>
                  {t('results_filters_qcStatus_passed')}
                </option>
                <option value='Pending'>
                  {t('results_filters_qcStatus_pending')}
                </option>
              </select>
            </div>
          </div>
        </div>
        <div id='right'>
          <div id='order-number' className='input'>
            <label>{t('results_filters_labels_orderNumber')}</label>
            <FilterTextInput
              value={filters.orderNumber}
              name='orderNumber'
              callback={update}
              config={{ placeholder: 'R-000000', maxLength: 12 }}
              save={save}
            />
          </div>
          <div id='sample-id' className='input'>
            <label>{t('results_filters_labels_sampleId')}</label>
            <FilterTextInput
              value={filters.specimenNumber}
              name='specimenNumber'
              callback={update}
              config={{ placeholder: 'SP-00000', maxLength: 12 }}
              save={save}
            />
          </div>
          {isSampleLevelDocumentsEnabled ? (
            <div id='results-id' className='input'>
              <label>{t('results_filters_labels_reportId')}</label>
              <FilterTextInput
                value={filters.resultName}
                name='resultName'
                callback={update}
                config={{ placeholder: `${prefix}00000000`, maxLength: 10 }}
                save={save}
              />
            </div>
          ) : null}

          <div id='controls'>
            <button
              id='filter-clear-btn'
              className='btn btn-light'
              onClick={() => clearAll()}
            >
              {t('results_filters_text_clearFilters')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
